/* eslint-disable no-restricted-imports */
/* eslint-disable @typescript-eslint/unbound-method */
import { useCallback, useMemo } from 'react';
import { useLogout } from './use-logout';
import { useResetFullyReadMarkers } from './TownsContext/useResetFullyReadMarkers';
import { useSendReadReceipt } from './TownsContext/useSendReadReceipt';
import { useTownsContext } from '../components/TownsContextProvider';
import { useCasablancaWalletSignIn, } from './use-casablanca-wallet-signin';
import { create } from 'zustand';
export const useTownsErrorStore = create((set) => ({
    errors: [],
    appendError: (error) => set((state) => ({
        errors: [...state.errors, error],
    })),
}));
export function useTownsClient() {
    const { getIsWalletRegisteredWithCasablanca, loginWithWalletToCasablanca, registerWalletWithCasablanca, } = useCasablancaWalletSignIn();
    const { client, clientSingleton, signerContext } = useTownsContext();
    const clientRunning = useMemo(() => signerContext !== undefined, [signerContext]);
    const logout = useLogout();
    const sendReadReceipt = useSendReadReceipt(client);
    const resetFullyReadMarkers = useResetFullyReadMarkers();
    const joinTown = useCallback(
    // if you call join town before you have a user in the stream node
    // join town will initialize your user after you have have minted in the space contract
    // capture and pass the space context here
    (spaceId, signer, onJoinFlowStatus, defaultUsername) => {
        if (!clientSingleton) {
            console.error('clientSingleton is undefined');
            return Promise.resolve(undefined);
        }
        return clientSingleton.joinTown(spaceId, signer, signerContext, onJoinFlowStatus, defaultUsername);
    }, [clientSingleton, signerContext]);
    const waitForCreateSpaceTransaction = useCallback(
    // if you call join town before you have a user in the stream node
    // join town will initialize your user after you have have minted in the space contract
    // capture and pass the space context here
    (context, defaultUsernames, onCreateSpaceFlowStatus) => {
        if (!clientSingleton) {
            console.error('clientSingleton is undefined');
            return Promise.resolve(undefined);
        }
        return clientSingleton.waitForCreateSpaceTransaction(context, signerContext, defaultUsernames, onCreateSpaceFlowStatus);
    }, [clientSingleton, signerContext]);
    return {
        chainId: client?.opts.baseChainId,
        client,
        clientRunning,
        clientSingleton,
        spaceDapp: clientSingleton?.spaceDapp,
        signerContext,
        createSpaceTransaction: useWithCatch(clientSingleton?.createSpaceTransaction),
        waitForCreateSpaceTransaction,
        createMediaStream: useWithCatch(clientSingleton?.createMediaStream),
        createChannelTransaction: useWithCatch(clientSingleton?.createChannelTransaction),
        waitForCreateChannelTransaction: useWithCatch(clientSingleton?.waitForCreateChannelTransaction),
        updateChannelTransaction: useWithCatch(clientSingleton?.updateChannelTransaction),
        setChannelPermissionOverridesTransaction: useWithCatch(clientSingleton?.setChannelPermissionOverridesTransaction),
        waitForSetChannelPermissionOverridesTransaction: useWithCatch(clientSingleton?.waitForSetChannelPermissionOverridesTransaction),
        clearChannelPermissionOverridesTransaction: useWithCatch(clientSingleton?.clearChannelPermissionOverridesTransaction),
        waitForClearChannelPermissionOverridesTransaction: useWithCatch(clientSingleton?.waitForSetChannelPermissionOverridesTransaction),
        waitForUpdateChannelTransaction: useWithCatch(clientSingleton?.waitForUpdateChannelTransaction),
        banTransaction: useWithCatch(clientSingleton?.banTransaction),
        unbanTransaction: useWithCatch(clientSingleton?.unbanTransaction),
        waitForBanUnbanTransaction: useWithCatch(clientSingleton?.waitForBanUnbanTransaction),
        walletAddressIsBanned: useWithCatch(clientSingleton?.walletAddressIsBanned),
        createDMChannel: useWithCatch(clientSingleton?.createDMChannel),
        createGDMChannel: useWithCatch(clientSingleton?.createGDMChannel),
        createRoleTransaction: useWithCatch(clientSingleton?.createRoleTransaction),
        waitForCreateRoleTransaction: useWithCatch(clientSingleton?.waitForCreateRoleTransaction),
        addRoleToChannelTransaction: useWithCatch(clientSingleton?.addRoleToChannelTransaction),
        waitForAddRoleToChannelTransaction: useWithCatch(clientSingleton?.waitForAddRoleToChannelTransaction),
        updateRoleTransaction: useWithCatch(clientSingleton?.updateRoleTransaction),
        waitForUpdateRoleTransaction: useWithCatch(clientSingleton?.waitForUpdateRoleTransaction),
        deleteRoleTransaction: useWithCatch(clientSingleton?.deleteRoleTransaction),
        waitForDeleteRoleTransaction: useWithCatch(clientSingleton?.waitForDeleteRoleTransaction),
        waitForUpdateSpaceInfoTransaction: useWithCatch(clientSingleton?.waitForUpdateSpaceInfoTransaction),
        updateSpaceInfoTransaction: useWithCatch(clientSingleton?.updateSpaceInfoTransaction),
        editSpaceMembershipTransaction: useWithCatch(clientSingleton?.editSpaceMembershipTransaction),
        waitForEditSpaceMembershipTransaction: useWithCatch(clientSingleton?.waitForEditSpaceMembershipTransaction),
        editMessage: useWithCatch(clientSingleton?.editMessage),
        getIsUsernameAvailable: useWithCatch(clientSingleton?.getIsUsernameAvailable),
        getIsWalletRegisteredWithCasablanca,
        getServerVersions: useWithCatch(clientSingleton?.getServerVersions),
        getUserProfileImage: useWithCatch(clientSingleton?.getUserProfileImage),
        updateUserBlock: useWithCatch(clientSingleton?.updateUserBlock),
        inviteUser: useWithCatch(clientSingleton?.inviteUser),
        joinRoom: useWithCatch(clientSingleton?.joinRoom),
        leaveRoom: useWithCatch(clientSingleton?.leave),
        loginWithWalletToCasablanca,
        logout,
        joinTown,
        pinMessage: useWithCatch(clientSingleton?.pinMessage),
        unpinMessage: useWithCatch(clientSingleton?.unpinMessage),
        redactEvent: useWithCatch(clientSingleton?.redactEvent),
        registerWalletWithCasablanca,
        removeUser: useWithCatch(clientSingleton?.removeUser),
        resetFullyReadMarkers,
        scrollback: useWithCatch(clientSingleton?.scrollback),
        scrollbackToEvent: useWithCatch(clientSingleton?.scrollbackToEvent),
        sendMessage: useWithCatch(clientSingleton?.sendMessage),
        retrySendMessage: useWithCatch(clientSingleton?.retrySendMessage),
        sendReaction: useWithCatch(clientSingleton?.sendReaction),
        sendMediaPayload: useWithCatch(clientSingleton?.sendMediaPayload),
        sendReadReceipt: useWithCatch(sendReadReceipt),
        sendTokenTransfer: useWithCatch(clientSingleton?.sendTokenTransfer),
        adminRedactMessage: useWithCatch(clientSingleton?.adminRedactMessage),
        setDisplayName: useWithCatch(clientSingleton?.setDisplayName),
        setRoomProperties: useWithCatch(clientSingleton?.setRoomProperties),
        setAvatarUrl: useWithCatch(clientSingleton?.setAvatarUrl),
        setHighPriorityStreams: useWithCatch(clientSingleton?.setHighPriorityStreams),
        setSpaceImage: useWithCatch(clientSingleton?.setSpaceImage),
        setUserProfileImage: useWithCatch(clientSingleton?.setUserProfileImage),
        setUserBio: useWithCatch(clientSingleton?.setUserBio),
        linkEOAToRootKey: useWithCatch(clientSingleton?.linkEOAToRootKey),
        linkCallerToRootKey: useWithCatch(clientSingleton?.linkCallerToRootKey),
        unlinkViaRootKey: useWithCatch(clientSingleton?.unlinkViaRootKey),
        unlinkViaCaller: useWithCatch(clientSingleton?.unlinkViaCaller),
        getLinkedWallets: useWithCatch(clientSingleton?.getLinkedWallets),
        getLinkedWalletsWithDelegations: useWithCatch(clientSingleton?.getLinkedWalletsWithDelegations),
        waitWalletLinkTransaction: useWithCatch(clientSingleton?.waitWalletLinkTransaction),
        prepayMembershipTransaction: useWithCatch(clientSingleton?.prepayMembershipTransaction),
        waitForPrepayMembershipTransaction: useWithCatch(clientSingleton?.waitForPrepayMembershipTransaction),
        transferAsset: useWithCatch(clientSingleton?.transferAsset),
        waitForTransferAssetTransaction: useWithCatch(clientSingleton?.waitForTransferAssetTransaction),
        refreshMetadataTransaction: useWithCatch(clientSingleton?.refreshMetadataTransaction),
        waitForRefreshMetadataTransaction: useWithCatch(clientSingleton?.waitForRefreshMetadataTransaction),
        tipTransaction: useWithCatch(clientSingleton?.tipTransaction),
        waitForTipTransaction: useWithCatch(clientSingleton?.waitForTipTransaction),
        checkInTransaction: useWithCatch(clientSingleton?.checkInTransaction),
        waitForCheckInTransaction: useWithCatch(clientSingleton?.waitForCheckInTransaction),
        sendTokenTransferOperationWithCallData: useWithCatch(clientSingleton?.sendTokenTransferOperationWithCallData),
        waitForUserOperationWithCallDataTransaction: useWithCatch(clientSingleton?.waitForUserOperationWithCallDataTransaction),
        reviewTransaction: useWithCatch(clientSingleton?.reviewTransaction),
        waitForReviewTransaction: useWithCatch(clientSingleton?.waitForReviewTransaction),
    };
}
const useWithCatch = (fn) => {
    const client = useTownsContext().clientSingleton;
    return useMemo(() => async (...args) => {
        if (fn && client) {
            try {
                const value = await fn.apply(client, args);
                return value;
            }
            catch (err) {
                // just give up
                console.error('Not a retryable error', err);
                return;
            }
        }
        else {
            console.error('client or fn is undefined', { fn, client });
        }
    }, [fn, client]);
};
//# sourceMappingURL=use-towns-client.js.map